import { useCallback } from "react";
import { clsx } from "clsx";

import { PaymentCreditCard } from "../../model/types/paymentCreditCard";

import { Icon } from "@/shared/ui/Icon";
import { Theme, useTheme } from "@/shared/lib/hooks/useTheme";

import AmericanExpressIcon from "@/shared/assets/payment/amex.svg";
import DinerClubIcon from "@/shared/assets/payment/dinerclub.svg";
import DiscoverIcon from "@/shared/assets/payment/discover.svg";
import JCBIcon from "@/shared/assets/payment/jcb.svg";
import MasterCardIcon from "@/shared/assets/payment/mastercard.svg";
import UnknownIcon from "@/shared/assets/payment/unknown.svg";
import VisaIcon from "@/shared/assets/payment/visa.svg";
import deleteIcon from "@/shared/assets/xicon.svg";

import cls from "./CreditCard.module.scss";

type Props = PaymentCreditCard & {
  handleDelete: (paymentId: string) => void;
  handleMakeDefault: (paymentId: string) => void;
  disableDelete?: boolean;
  isDefault?: boolean;
};

const cardIconMap = {
  amex: AmericanExpressIcon,
  diners: DinerClubIcon,
  discover: DiscoverIcon,
  jcb: JCBIcon,
  mastercard: MasterCardIcon,
  visa: VisaIcon,
  unknown: UnknownIcon,
};

export function CreditCard({ id, card, isDefault, handleDelete, handleMakeDefault, disableDelete }: Props) {
  const { theme } = useTheme();

  const iconSrc = cardIconMap[card.brand] ?? UnknownIcon;

  const onClickMakeDefault = useCallback(() => {
    if (isDefault) {
      return;
    }

    handleMakeDefault(id);
  }, [isDefault, id, handleMakeDefault]);

  return (
    <div className={clsx(cls.CreditCard, { [cls.isDark]: theme === Theme.Dark })}>
      <Icon Svg={iconSrc} />
      <div className={cls.Details}>
        <div>Ends in {card.last4}</div>
        <div>
          Expires {card.expMonth}/{card.expYear}
        </div>
        <div className={clsx(cls.MakeDefaultButton, { [cls.isDefault]: isDefault })} onClick={onClickMakeDefault}>
          {isDefault ? "(DEFAULT)" : "MAKE DEFAULT"}
        </div>
      </div>
      {!disableDelete && (
        <Icon className={cls.DeleteButton} Svg={deleteIcon} width={24} height={24} onClick={() => handleDelete(id)} />
      )}
    </div>
  );
}
